declare global {
    interface Window {
        /**
         * Opened by the host app once its advertising pixels have booted, or as
         * soon as consent suppresses them.
         *
         * Absent on pages that define their pixels synchronously in the document
         * head, such as Business Manager, where there is nothing to wait for.
         */
        __apAdvertisingReady?: {
            promise: Promise<void>;
        };
    }
}

/**
 * Wait for the advertising pixels to finish booting.
 *
 * Events are routinely raised before the pixels exist: the tracker is created
 * and flushes its queued first page view a commit earlier than the pixels
 * mount. Without this the vendor global is still undefined and the event is
 * lost.
 */
export const awaitAdvertisingPixels = async (): Promise<void> => {
    await window.__apAdvertisingReady?.promise;
};

/**
 * Report a vendor global that never arrived, once per tracker instance.
 *
 * Tracker calls run inside `Promise.allSettled`, so touching an undefined
 * global rejects into nothing and drops the event without a trace. Warning
 * instead keeps a pixel that fails to boot visible.
 */
export const createMissingPixelWarning = (
    vendor: string,
): ((eventName: string) => void) => {
    let warned = false;

    return (eventName: string): void => {
        if (warned) return;
        warned = true;
        console.warn(
            `${vendor} pixel is not loaded; dropping "${eventName}" and any later ${vendor} events. ` +
                'Expected when the visitor has opted out of sale/sharing, otherwise the pixel failed to boot.',
        );
    };
};
